import React, { useState } from 'react';
import { TeamMember, Deal, Stage } from '../types/pipeline';
import { PipelineCard } from './PipelineCard';
import { STAGES } from '../data/mockPipelineData';
import { ChevronRight, ChevronDown, ChevronUp, Plus } from 'lucide-react';
import { ViewDensity } from './ViewControls';
interface TeamMemberLanesProps {
  members: TeamMember[];
  deals: Deal[];
  density: ViewDensity;
  collapsedLanes: Set<string>;
  onToggleLane: (memberId: string) => void;
  onDealClick: (deal: Deal) => void;
}
export function TeamMemberLanes({
  members,
  deals,
  density,
  collapsedLanes,
  onToggleLane,
  onDealClick
}: TeamMemberLanesProps) {
  const [expandedStages, setExpandedStages] = useState<Set<string>>(new Set());
  // Group deals by member and stage
  const getDealsForMemberAndStage = (memberId: string, stage: Stage) => {
    return deals.filter(d => d.assignedTo === memberId && d.stage === stage);
  };
  // Calculate total volume for a member
  const getMemberVolume = (memberId: string) => {
    const total = deals.filter(d => d.assignedTo === memberId).reduce((sum, d) => sum + d.amount, 0);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      notation: 'compact',
      maximumFractionDigits: 1
    }).format(total);
  };
  // Get member deal count
  const getMemberDealCount = (memberId: string) => {
    return deals.filter(d => d.assignedTo === memberId).length;
  };
  // Get stage breakdown for collapsed view
  const getMemberStageBreakdown = (memberId: string) => {
    const breakdown: Record<Stage, number> = {
      'Pre-qual': 0,
      Application: 0,
      Processing: 0,
      Underwriting: 0,
      'Clear to Close': 0
    };
    deals.filter(d => d.assignedTo === memberId).forEach(d => {
      breakdown[d.stage]++;
    });
    return breakdown;
  };
  const isCollapsed = (memberId: string) => collapsedLanes.has(memberId);
  const toggleStageExpansion = (key: string) => {
    setExpandedStages(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };
  const isStageExpanded = (memberId: string, stage: Stage) => {
    return expandedStages.has(`${memberId}-${stage}`);
  };
  // Determine how many deals to show initially based on density
  const getInitialShowCount = () => {
    if (density === 'compact') return 2;
    if (density === 'comfortable') return 3;
    return 4;
  };
  return <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden flex flex-col" data-id="element-327">
      {/* Header Row - Stages */}
      <div className="flex border-b border-slate-200 bg-slate-50/80 backdrop-blur sticky top-0 z-10" data-id="element-328">
        <div className="w-64 flex-shrink-0 p-4 font-semibold text-slate-500 text-sm uppercase tracking-wider border-r border-slate-200" data-id="element-329">
          Team Member
        </div>
        <div className="flex-1 flex" data-id="element-330">
          {STAGES.map((stage, index) => <div key={stage} className={`flex-1 p-4 font-semibold text-sm uppercase tracking-wider flex items-center
                ${index % 2 === 0 ? 'bg-slate-50/50' : 'bg-white'}
                border-r border-slate-100 last:border-r-0
              `} data-id="element-331">
              <div className={`w-2 h-2 rounded-full mr-2 
                ${index === 0 ? 'bg-blue-400' : index === 1 ? 'bg-indigo-400' : index === 2 ? 'bg-violet-400' : index === 3 ? 'bg-fuchsia-400' : 'bg-emerald-400'}`} data-id="element-332" />
              <span className="text-slate-600" data-id="element-333">{stage}</span>
              <span className="ml-auto bg-slate-200 text-slate-600 text-xs py-0.5 px-2 rounded-full" data-id="element-334">
                {deals.filter(d => d.stage === stage).length}
              </span>
            </div>)}
        </div>
      </div>

      {/* Swim Lanes */}
      <div className="overflow-y-auto flex-1" data-id="element-335">
        {members.map(member => {
        const collapsed = isCollapsed(member.id);
        const dealCount = getMemberDealCount(member.id);
        const stageBreakdown = getMemberStageBreakdown(member.id);
        return <div key={member.id} className="flex border-b border-slate-100 last:border-b-0 hover:bg-slate-50/30 transition-colors group" data-id="element-336">
              {/* Member Profile Sidebar */}
              <div className="w-64 flex-shrink-0 border-r border-slate-200 bg-white sticky left-0 z-10 group-hover:bg-slate-50/30 transition-colors" data-id="element-337">
                <div className={`${collapsed ? 'p-3' : 'p-6'}`} data-id="element-338">
                  <div className="flex items-center justify-between mb-3" data-id="element-339">
                    <div className="flex items-center flex-1 min-w-0" data-id="element-340">
                      <div className="relative flex-shrink-0" data-id="element-341">
                        <img src={member.avatar} alt={member.name} className={`rounded-full object-cover border-2 border-white shadow-sm ${collapsed ? 'w-10 h-10' : 'w-12 h-12'}`} data-id="element-342" />
                        <div className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-white rounded-full" data-id="element-343"></div>
                      </div>
                      <div className="ml-3 flex-1 min-w-0" data-id="element-344">
                        <h3 className={`font-bold text-slate-900 truncate ${collapsed ? 'text-xs' : 'text-sm'}`} data-id="element-345">
                          {member.name}
                        </h3>
                        {!collapsed && <p className="text-xs text-slate-500" data-id="element-346">
                            {member.role}
                          </p>}
                      </div>
                    </div>
                    <button onClick={() => onToggleLane(member.id)} className="ml-2 p-1 hover:bg-slate-200 rounded transition-colors flex-shrink-0" aria-label={collapsed ? 'Expand lane' : 'Collapse lane'} data-id="element-347">
                      {collapsed ? <ChevronDown className="w-4 h-4 text-slate-500" data-id="element-348" /> : <ChevronUp className="w-4 h-4 text-slate-500" data-id="element-349" />}
                    </button>
                  </div>

                  <div className={`space-y-2 ${collapsed ? 'text-xs' : ''}`} data-id="element-350">
                    <div className="flex items-center justify-between text-xs text-slate-500 bg-slate-100 rounded-lg p-2" data-id="element-351">
                      <span data-id="element-352">Pipeline Vol:</span>
                      <span className="font-bold text-slate-700" data-id="element-353">
                        {getMemberVolume(member.id)}
                      </span>
                    </div>

                    {collapsed && <div className="flex items-center justify-between text-xs bg-blue-50 text-blue-700 rounded-lg p-2" data-id="element-354">
                        <span className="font-medium" data-id="element-355">Total Deals:</span>
                        <span className="font-bold" data-id="element-356">{dealCount}</span>
                      </div>}
                  </div>
                </div>
              </div>

              {/* Stage Lanes or Collapsed Summary */}
              {collapsed ? <div className="flex-1 flex items-center px-6 py-4" data-id="element-357">
                  <div className="flex items-center space-x-4 flex-wrap" data-id="element-358">
                    {STAGES.map((stage, index) => {
                const count = stageBreakdown[stage];
                if (count === 0) return null;
                return <div key={stage} className="flex items-center space-x-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200" data-id="element-359">
                          <div className={`w-2 h-2 rounded-full ${index === 0 ? 'bg-blue-400' : index === 1 ? 'bg-indigo-400' : index === 2 ? 'bg-violet-400' : index === 3 ? 'bg-fuchsia-400' : 'bg-emerald-400'}`} data-id="element-360" />
                          <span className="text-xs font-medium text-slate-600" data-id="element-361">
                            {stage}
                          </span>
                          <span className="text-xs font-bold text-slate-900 bg-white px-2 py-0.5 rounded-full" data-id="element-362">
                            {count}
                          </span>
                        </div>;
              })}
                  </div>
                </div> : <div className="flex-1 flex" data-id="element-363">
                  {STAGES.map((stage, index) => {
              const stageDeals = getDealsForMemberAndStage(member.id, stage);
              const initialShowCount = getInitialShowCount();
              const hasMore = stageDeals.length > initialShowCount;
              const expanded = isStageExpanded(member.id, stage);
              const visibleDeals = expanded ? stageDeals : stageDeals.slice(0, initialShowCount);
              const stageKey = `${member.id}-${stage}`;
              return <div key={stage} className={`flex-1 border-r border-slate-100 last:border-r-0 relative
                          ${index % 2 === 0 ? 'bg-slate-50/30' : 'bg-white'}
                          ${density === 'compact' ? 'p-2' : density === 'comfortable' ? 'p-3' : 'p-4'}
                        `} data-id="element-364">
                        {/* Flow Arrow Background */}
                        {index < STAGES.length - 1 && stageDeals.length > 0 && <div className="absolute top-1/2 -right-3 z-0 text-slate-100 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-365">
                            <ChevronRight className="w-6 h-6" data-id="element-366" />
                          </div>}

                        {/* Vertical Stack of Deals */}
                        <div className={`space-y-2 ${density === 'compact' ? 'space-y-1.5' : 'space-y-2'}`} data-id="element-367">
                          {stageDeals.length > 0 ? <>
                              {visibleDeals.map(deal => <PipelineCard key={deal.id} deal={deal} density={density} variant="stacked" onClick={() => onDealClick(deal)} data-id="element-368" />)}

                              {hasMore && <button onClick={() => toggleStageExpansion(stageKey)} className="w-full py-2 px-3 text-xs font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors flex items-center justify-center space-x-1" data-id="element-369">
                                  {expanded ? <>
                                      <ChevronUp className="w-3 h-3" data-id="element-370" />
                                      <span data-id="element-371">Show Less</span>
                                    </> : <>
                                      <Plus className="w-3 h-3" data-id="element-372" />
                                      <span data-id="element-373">
                                        Show{' '}
                                        {stageDeals.length - initialShowCount}{' '}
                                        More
                                      </span>
                                    </>}
                                </button>}
                            </> : <div className={`w-full border-2 border-dashed border-slate-100 rounded-lg flex items-center justify-center ${density === 'compact' ? 'h-12' : density === 'comfortable' ? 'h-16' : 'h-20'}`} data-id="element-374">
                              <span className="text-xs text-slate-300 font-medium" data-id="element-375">
                                No deals
                              </span>
                            </div>}
                        </div>
                      </div>;
            })}
                </div>}
            </div>;
      })}
      </div>
    </div>;
}